import type { DifficultyConfig, MathOperation, Operator } from '@/types/models';

const randInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = <T>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];

export function generateOperation(cfg: DifficultyConfig): MathOperation {
  const op: Operator = pick(cfg.operators);
  const maxDots = cfg.maxDots;
  const numberMax = Math.max(2, cfg.numberMax);

  for (let attempt = 0; attempt < 30; attempt++) {
    const candidate = tryGenerate(op, numberMax, maxDots);
    if (candidate) return candidate;
  }

  return { a: 1, b: 1, op: '+', result: 2 };
}

function tryGenerate(op: Operator, numberMax: number, maxDots: number): MathOperation | null {
  switch (op) {
    case '+': {
      const a = randInt(1, Math.min(numberMax, maxDots - 1));
      const b = randInt(1, Math.min(numberMax, maxDots - a));
      const result = a + b;
      if (result < 1 || result > maxDots) return null;
      return { a, b, op, result };
    }
    case '-': {
      let a = randInt(2, numberMax);
      let b = randInt(1, numberMax);
      if (b > a) [a, b] = [b, a];
      const result = a - b;
      if (result < 1 || result > maxDots) return null;
      return { a, b, op, result };
    }
    case '×': {
      const factorMax = Math.max(2, Math.floor(Math.sqrt(maxDots)));
      const a = randInt(1, factorMax);
      const b = randInt(1, factorMax);
      const result = a * b;
      if (result < 1 || result > maxDots) return null;
      return { a, b, op, result };
    }
    case '÷': {
      const result = randInt(1, Math.min(maxDots, 9));
      const b = randInt(1, Math.min(numberMax, 9));
      const a = result * b;
      if (a > numberMax * 4) return null;
      return { a, b, op, result };
    }
  }
}
